import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { ChevronDown, FileText, Download, BookOpen, ShieldCheck } from "lucide-react";

const RESOURCES = [
  { title: "Leduro Master Catalogue 2026", size: "12.4 MB", type: "PDF", testid: "res-master" },
  { title: "Photometric Data Pack (IES / LDT)", size: "84 MB", type: "ZIP", testid: "res-photo" },
  { title: "EN 60598 Conformity Certificates", size: "3.1 MB", type: "PDF", testid: "res-en60598" },
  { title: "Project Quote Form", size: "120 KB", type: "DOCX", testid: "res-quote-form" },
  { title: "Return & RMA Procedure", size: "210 KB", type: "PDF", testid: "res-rma" },
  { title: "Brand & Packaging Guide", size: "8.6 MB", type: "PDF", testid: "res-brand" },
];

const FAQS = [
  {
    q: "Do you sell to private individuals?",
    a: "No. Leduro is a strict B2B supplier. Our minimum order is one full case, and trade documents (VAT / company registry) are required at registration.",
  },
  {
    q: "How is volume pricing structured?",
    a: "Pricing tiers begin at 50 units per SKU and scale to 1000+. Project pricing is available for tendered installations — request a quote directly from the product page.",
  },
  {
    q: "What is your standard lead time?",
    a: "Stock SKUs ship within 24 hours from Rīga or Tallinn. Custom or special-order ranges typically take 6–8 weeks ex-factory.",
  },
  {
    q: "Are IES / LDT files available for all products?",
    a: "Yes. Every active Leduro SKU has measured IES and LDT files. They are available in the resources section above or on request from your account engineer.",
  },
  {
    q: "How does the warranty process work?",
    a: "Warranty claims are processed within 5 business days. Replacements ship from regional stock — no on-site visits required for trade buyers.",
  },
  {
    q: "Can I order Leduro under my own brand?",
    a: "Private-label is available from 10,000 unit annual commitments. Contact our trade team for the OEM/ODM information pack.",
  },
];

export default function UsefulInformation() {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="useful-info-page">
      <PageHero
        eyebrow="Useful Information"
        title="Datasheets, certificates and trade documents."
        subtitle="Everything an electrician, specifier or procurement officer needs — in one place."
        crumbs={[{ label: "Useful Information" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <span className="leduro-eyebrow">— Downloads</span>
            <h2 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-4xl">
              Trade resources
            </h2>
            <p className="mt-4 text-leduro-muted">Click to download. Logged-in trade buyers receive every datasheet by email automatically.</p>

            <div className="mt-8 border border-leduro-border">
              {RESOURCES.map((r, i) => (
                <button
                  key={r.title}
                  data-testid={r.testid}
                  className={`w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-leduro-surface transition-colors ${i !== RESOURCES.length - 1 ? "border-b border-leduro-border" : ""}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-11 w-11 grid place-items-center bg-leduro-surface border border-leduro-border shrink-0">
                      <FileText className="h-5 w-5 text-leduro-orange" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-leduro-navy truncate">{r.title}</div>
                      <div className="text-xs text-leduro-muted mt-0.5">{r.type} · {r.size}</div>
                    </div>
                  </div>
                  <Download className="h-4 w-4 text-leduro-navy shrink-0" />
                </button>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-leduro-navy text-white p-8">
              <BookOpen className="h-8 w-8 text-leduro-orange" />
              <h3 className="mt-5 font-heading font-bold text-xl">Technical glossary</h3>
              <p className="mt-3 text-white/75 text-sm leading-relaxed">
                Quick reference on the four terms that define LED quality:
              </p>
              <dl className="mt-5 space-y-4 text-sm">
                {[
                  ["lm/W", "Luminous efficacy. The higher the number, the more light per watt."],
                  ["CRI", "Colour Rendering Index. 80+ is professional, 90+ is for retail / hospitality."],
                  ["UGR", "Unified Glare Rating. Below 19 is required for office work."],
                  ["IP / IK", "Dust+water and impact resistance ratings. IP65/IK08 = outdoor-grade."],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-leduro-orange font-mono font-bold tracking-wider">{k}</dt>
                    <dd className="text-white/80 leading-relaxed mt-1">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-6 border border-leduro-border p-6">
              <ShieldCheck className="h-7 w-7 text-leduro-orange" />
              <h3 className="mt-3 font-heading font-bold text-leduro-navy">Certifications</h3>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {["CE", "RoHS", "REACH", "ENEC", "EAC", "CB Scheme"].map((c) => (
                  <li key={c} className="border border-leduro-border px-3 py-2 text-leduro-navy font-bold text-center">{c}</li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-leduro-surface py-20" data-testid="faq-section">
        <div className="leduro-container max-w-3xl">
          <span className="leduro-eyebrow">— FAQ</span>
          <h2 className="mt-3 font-heading font-black tracking-tighter text-leduro-navy text-3xl md:text-4xl">
            Trade questions, answered.
          </h2>
          <div className="mt-10 border border-leduro-border bg-white">
            {FAQS.map((f, i) => (
              <div key={i} className={i !== FAQS.length - 1 ? "border-b border-leduro-border" : ""}>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left"
                >
                  <span className="font-heading font-bold text-leduro-navy">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 text-leduro-orange transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                {open === i && (
                  <div className="px-5 pb-5 text-sm text-leduro-muted leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
